<template>
	<div
		:class="[
			'py-300 py-500-md',
			{
				'bg-color-white': data.theme === 'light',
				'bg-color-content-primary': data.theme === 'dark'
			}
		]"
	>
		<MarketingPageHeader :header="header" :theme="data.theme" :is-secondary="secondaryHeader"></MarketingPageHeader>
		<div class="py-100 mt-0-smo mb-150 mb-300-md container">
			<LottieAnimation v-if="lottieFileUrl" :lottie-file="lottieFileUrl" :max-retries="2" />
			<img v-else :src="fallbackImage" alt="end to end technology graphic" />

			<div
				:class="[
					'mt-300 col-12 col-8-md col-offset-2-md',
					data.theme === 'light' ? 'text-color-content-secondary' : 'text-color-white',
					textAlignment
				]"
			>
				<p class="body-xl" v-html="deorphanText(data.diagramSubtext)"></p>
				<div v-if="data.content">
					<p
						v-for="(c, idx) in data.content"
						:key="idx"
						class="mt-100 text-color-gray-40 body-lg"
						v-html="deorphanText(c)"
					/>
				</div>
				<p v-if="data.cta" class="mt-200">
					<BaseCtaLink classes="body-lg" icon="arrow" :is-orange="true" :link="data.cta" />
				</p>
			</div>
		</div>
		<div v-if="data.cards" class="container">
			<MarketingSiteCardRow :cards="data.cards" :theme="data.theme" class="mt-100" />
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { deorphanText } from '@filters/shared-filters';
import { LandingPageHeader } from 'types/layout';
import LottieAnimation from '@components/layout/lottie-animation.vue';
import MarketingPageHeader from '@components/marketing-site/marketing-page-header.vue';
import MarketingSiteCardRow from '@components/marketing-site/marketing-site-card-row.vue';
import { useAppStore } from '@stores/app';
import { WhyFundriseSectionData } from 'types/marketing-site';

interface Props {
	data: WhyFundriseSectionData;
	secondaryHeader?: boolean;
	alignment?: 'left' | 'right' | 'center';
}

const props = withDefaults(defineProps<Props>(), {
	secondaryHeader: false,
	alignment: 'center'
});

const appStore = useAppStore();

const isMobile = computed((): boolean => {
	return appStore.isMobile;
});

const header = computed((): LandingPageHeader => {
	return {
		title: props.data.header.title,
		heading: props.data.header.heading
	};
});

const lottieFileUrl = ref<string | undefined>(
	isMobile.value ? props.data.video.diagramAnimationMobile : props.data.video.diagramAnimation
);

const fallbackImage = computed((): string | undefined => {
	return isMobile.value ? props.data.video.fallbackImageMobile : props.data.video.fallbackImage;
});

const textAlignment = computed((): string => {
	switch (props.alignment) {
		case 'center':
			return 'text-align-center';
		case 'right':
			return 'text-align-right';
		case 'left':
		default:
			return 'text-align-left';
	}
});

watch(isMobile, () => {
	lottieFileUrl.value = undefined;

	setTimeout(() => {
		lottieFileUrl.value = isMobile.value
			? props.data.video.diagramAnimationMobile
			: props.data.video.diagramAnimation;
	}, 0);
});
</script>
<style lang="scss" scoped>
video {
	max-width: 100%;
}
</style>
